import { useEffect, useState } from 'react';
import {
  Users, UserPlus, Search, MoreHorizontal, RotateCcw, Ban,
  CheckCircle2, Key, Upload,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const ROLES = [
  { value: 'player', label: 'Player' },
  { value: 'scorer', label: 'Scorer' },
  { value: 'tournament_manager', label: 'Tournament Manager' },
  { value: 'org_admin', label: 'Org Admin' },
];

const StatusBadge = ({ status }) => {
  const isActive = status === 'active' || !status;
  return (
    <Badge className={`border ${isActive
      ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]'
      : 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]'
    }`}>
      {isActive ? 'Active' : 'Disabled'}
    </Badge>
  );
};

const RoleSelector = ({ selected, onChange }) => (
  <div className="grid grid-cols-2 gap-2">
    {ROLES.map((role) => {
      const isSelected = selected.includes(role.value);
      return (
        <div
          key={role.value}
          onClick={() => {
            onChange(
              isSelected ? selected.filter((r) => r !== role.value) : [...selected, role.value]
            );
          }}
          className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border transition-colors ${
            isSelected
              ? 'bg-[rgba(245,197,24,0.12)] border-[rgba(245,197,24,0.35)]'
              : 'bg-[#1d2230] border-[#252a37] hover:border-[#8a93a3]'
          }`}
        >
          <div
            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              isSelected ? 'bg-[#f5c518] border-[#f5c518]' : 'border-[#8a93a3]'
            }`}
          >
            {isSelected && <span className="text-[#0b0f18] text-[10px] font-bold leading-none">✓</span>}
          </div>
          <span className={`text-xs font-medium ${isSelected ? 'text-[#f0f2f7]' : 'text-[#8a93a3]'}`}>
            {role.label}
          </span>
        </div>
      );
    })}
  </div>
);

export default function UsersPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ phone: '', display_name: '', roles: ['player'] });

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPhones, setBulkPhones] = useState('');
  const [bulkRoles, setBulkRoles] = useState(['player']);

  const [defaultPwOpen, setDefaultPwOpen] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [newDefaultPw, setNewDefaultPw] = useState('');
  const [savingDefaultPw, setSavingDefaultPw] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchDefaultPassword();
  }, [orgSlug]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/users`);
      const data = response.data;
      setUsers(Array.isArray(data) ? data : data.items || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultPassword = async () => {
    try {
      const response = await api.get(`/orgs/${orgSlug}/default-password`);
      setDefaultPassword(response.data?.default_password || '');
    } catch (_) {}
  };

  const handleInvite = async () => {
    if (!inviteForm.phone.trim()) { toast.error('Phone number is required'); return; }
    if (inviteForm.roles.length === 0) { toast.error('Select at least one role'); return; }
    setInviting(true);
    try {
      await api.post(`/orgs/${orgSlug}/users`, {
        phone: inviteForm.phone.trim(),
        display_name: inviteForm.display_name || undefined,
        roles: inviteForm.roles,
      });
      toast.success('User invited successfully');
      setInviteOpen(false);
      setInviteForm({ phone: '', display_name: '', roles: ['player'] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleBulkInvite = async () => {
    const phones = bulkPhones.split('\n').map((p) => p.trim()).filter(Boolean);
    if (phones.length === 0) { toast.error('Enter at least one phone number'); return; }
    if (phones.length > 500) { toast.error('Maximum 500 phones per request'); return; }
    if (bulkRoles.length === 0) { toast.error('Select at least one role'); return; }
    setInviting(true);
    try {
      await api.post(`/orgs/${orgSlug}/users/bulk`, { phones, roles: bulkRoles });
      toast.success(`${phones.length} user${phones.length > 1 ? 's' : ''} invited`);
      setBulkOpen(false);
      setBulkPhones('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk invite failed');
    } finally {
      setInviting(false);
    }
  };

  const handleResetPassword = async (userId, displayName) => {
    if (!confirm(`Reset password for ${displayName || userId} to org default?`)) return;
    try {
      await api.post(`/orgs/${orgSlug}/users/${userId}/reset-password`);
      toast.success('Password reset to org default');
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleStatus = async (user) => {
    const isActive = user.status === 'active' || !user.status;
    const action = isActive ? 'disable' : 'enable';
    if (!confirm(`${isActive ? 'Disable' : 'Enable'} ${user.display_name || user.phone}?`)) return;
    try {
      await api.post(`/orgs/${orgSlug}/users/${user.id}/${action}`);
      toast.success(`User ${action}d`);
      fetchUsers();
    } catch {
      toast.error(`Failed to ${action} user`);
    }
  };

  const handleSaveDefaultPw = async () => {
    if (!newDefaultPw || newDefaultPw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingDefaultPw(true);
    try {
      await api.put(`/orgs/${orgSlug}/default-password`, { password: newDefaultPw });
      toast.success('Default password updated');
      setDefaultPassword(newDefaultPw);
      setNewDefaultPw('');
      setDefaultPwOpen(false);
    } catch {
      toast.error('Failed to update default password');
    } finally {
      setSavingDefaultPw(false);
    }
  };

  const getRoles = (user) =>
    user.roles || user.memberships?.[0]?.roles || [];

  const filteredUsers = users.filter(
    (u) =>
      u.display_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search)
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Users</h1>
          <p className="text-[#8a93a3]">Manage invited users and their roles</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setDefaultPwOpen(true)}
            className="border-[#252a37] text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="users-default-password-button"
          >
            <Key className="h-4 w-4 mr-2" />
            Default Password
          </Button>
          <Button
            variant="outline"
            onClick={() => setBulkOpen(true)}
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="users-bulk-invite-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Invite
          </Button>
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
            data-testid="users-invite-button"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Default password banner */}
      {defaultPassword && (
        <Card className="bg-[#161a26] border-[#252a37] mb-6">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Key className="h-5 w-5 text-[#f5c518] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#f0f2f7]">Org Default Password</p>
                <p className="text-xs text-[#8a93a3]">
                  New invites and password resets use this
                </p>
              </div>
            </div>
            <code className="bg-[#1d2230] px-3 py-1.5 rounded text-[#f5c518] font-mono text-sm">
              {defaultPassword}
            </code>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or phone..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
            data-testid="users-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <Card className="bg-[#161a26] border-[#252a37]">
        <ScrollArea className="w-full">
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-9 w-9 rounded-full bg-[#1d2230]" />
                  <Skeleton className="h-4 w-32 bg-[#1d2230]" />
                  <Skeleton className="h-4 w-24 bg-[#1d2230]" />
                  <Skeleton className="h-6 w-16 bg-[#1d2230]" />
                </div>
              ))}
            </div>
          ) : filteredUsers.length > 0 ? (
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="border-b border-[#252a37] bg-[#1d2230]/50">
                  <TableHead className="text-xs uppercase text-[#8a93a3]">User</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Phone</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Roles</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Status</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3] text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => {
                  const isActive = user.status === 'active' || !user.status;
                  return (
                    <TableRow
                      key={user.id}
                      className="border-b border-[#252a37] hover:bg-[#1d2230]/30"
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-[#1d2230] flex items-center justify-center text-[#f5c518] font-medium text-sm flex-shrink-0">
                            {(user.display_name || user.phone)?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <span className="font-medium text-[#f0f2f7]">
                            {user.display_name || 'Unnamed'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#8a93a3]">
                        {user.phone || '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {getRoles(user).map((role) => (
                            <Badge
                              key={role}
                              className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs"
                            >
                              {role.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={user.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-[#8a93a3] hover:text-[#f0f2f7]"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#161a26] border-[#252a37]">
                            <DropdownMenuItem
                              onClick={() => handleResetPassword(user.id, user.display_name)}
                              className="text-[#f0f2f7] focus:bg-[#1d2230] cursor-pointer"
                              data-testid="user-reset-password-button"
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-[#252a37]" />
                            <DropdownMenuItem
                              onClick={() => handleToggleStatus(user)}
                              className={`cursor-pointer focus:bg-[#1d2230] ${
                                isActive ? 'text-[#ef4444]' : 'text-[#22c55e]'
                              }`}
                              data-testid="user-toggle-status-button"
                            >
                              {isActive ? (
                                <>
                                  <Ban className="h-4 w-4 mr-2" />
                                  Disable User
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Enable User
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
                {search ? 'No users found' : 'No users invited yet'}
              </h3>
              <p className="text-[#8a93a3] mb-4">
                {search ? 'Try adjusting your search' : 'Invite users to get started'}
              </p>
              {!search && (
                <Button
                  onClick={() => setInviteOpen(true)}
                  className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite First User
                </Button>
              )}
            </div>
          )}
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37]" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#f5c518]" />
              Invite User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3]">Phone Number *</Label>
              <Input
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="+91 98765 43210"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="invite-phone-input"
              />
            </div>
            <div>
              <Label className="text-[#8a93a3]">Display Name (optional)</Label>
              <Input
                value={inviteForm.display_name}
                onChange={(e) => setInviteForm({ ...inviteForm, display_name: e.target.value })}
                placeholder="e.g., Rahul Sharma"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Roles *</Label>
              <RoleSelector
                selected={inviteForm.roles}
                onChange={(roles) => setInviteForm({ ...inviteForm, roles })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              className="border-[#252a37] text-[#f0f2f7]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={inviting}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="invite-submit-button"
            >
              {inviting ? 'Inviting...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Invite Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37]" data-testid="bulk-invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#f5c518]" />
              Bulk Invite
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3] mb-2 block">
                Phone Numbers (one per line, max 500)
              </Label>
              <textarea
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                placeholder={'+919876543210\n+919876543211\n+919876543212'}
                rows={8}
                className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md p-3 text-sm font-mono placeholder:text-[#8a93a3] focus:outline-none focus:ring-1 focus:ring-[rgba(245,197,24,0.35)] resize-none"
                data-testid="bulk-phones-input"
              />
              <p className="mt-1 text-xs text-[#8a93a3]">
                {bulkPhones.split('\n').filter((p) => p.trim()).length} numbers entered
              </p>
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Roles for all users</Label>
              <RoleSelector selected={bulkRoles} onChange={setBulkRoles} />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkOpen(false)}
              className="border-[#252a37] text-[#f0f2f7]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkInvite}
              disabled={inviting}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="bulk-invite-submit-button"
            >
              {inviting ? 'Inviting...' : 'Invite All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Default Password Dialog */}
      <Dialog open={defaultPwOpen} onOpenChange={setDefaultPwOpen}>
        <DialogContent
          className="bg-[#161a26] border-[#252a37]"
          data-testid="default-password-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Key className="h-5 w-5 text-[#f5c518]" />
              Org Default Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-4">
              <p className="text-xs text-[#8a93a3] mb-2">Current default password</p>
              <code className="text-[#f5c518] font-mono text-sm">
                {defaultPassword || 'Not set'}
              </code>
            </div>
            <Separator className="bg-[#252a37]" />
            <div>
              <Label className="text-[#8a93a3]">Set New Default Password</Label>
              <Input
                type="text"
                value={newDefaultPw}
                onChange={(e) => setNewDefaultPw(e.target.value)}
                placeholder="Minimum 6 characters"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="new-default-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDefaultPwOpen(false)}
              className="border-[#252a37] text-[#f0f2f7]"
            >
              Close
            </Button>
            <Button
              onClick={handleSaveDefaultPw}
              disabled={savingDefaultPw}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="save-default-password-button"
            >
              {savingDefaultPw ? 'Saving...' : 'Save Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
